import type {
  PersonalFinancialStatement,
  PfsIncome,
  PfsTotals,
  RealEstateOwnedRow,
} from '@/types/PfsType';

/** Mirrors services/pfs.service.js computePfsTotals — computed live from draft state, never trusted from a stored value. */

function sum<T>(rows: T[] | undefined, key: keyof T): number {
  return (rows || []).reduce((acc, r) => acc + (Number(r[key]) || 0), 0);
}

function incomeTotal(income: PfsIncome | undefined): number {
  if (!income) return 0;
  const alimony = income.alimonyChildSupportDisclosed
    ? Number(income.alimonyChildSupport) || 0
    : 0;
  return (
    (Number(income.baseEmploymentIncome) || 0) +
    (Number(income.overtime) || 0) +
    (Number(income.commissions) || 0) +
    (Number(income.dividendsInterest) || 0) +
    (Number(income.rentalIncome) || 0) +
    alimony +
    (Number(income.otherIncome1) || 0) +
    (Number(income.otherIncome2) || 0) +
    (Number(income.otherIncome3) || 0)
  );
}

function realEstateMortgageBalanceTotal(rows: RealEstateOwnedRow[]): number {
  return rows.reduce((acc, r) => {
    const m1 = Number(r.mortgage1?.balance) || 0;
    const m2 = Number(r.mortgage2?.balance) || 0;
    return acc + m1 + m2;
  }, 0);
}

export function computePfsTotalsClient(
  pfs: PersonalFinancialStatement | null | undefined,
  realEstateOwned: RealEstateOwnedRow[] = []
): PfsTotals {
  const applicantIncome = incomeTotal(pfs?.applicantIncome);
  const spouseIncome = incomeTotal(pfs?.spouseIncome);

  const cash = sum(pfs?.cashAccounts, 'currentBalance');
  const investments = sum(pfs?.investments, 'currentValue');
  const otherAssets = sum(pfs?.otherAssets, 'currentValue');
  const businessNetWorth = Number(pfs?.businessNetWorth) || 0;
  const realEstateValue = sum(realEstateOwned, 'marketValue');

  const revolvingCredit = sum(pfs?.revolvingCredit, 'currentBalance');
  const installmentLoans = sum(pfs?.installmentLoans, 'currentBalance');
  const mortgageLoans = realEstateMortgageBalanceTotal(realEstateOwned);
  const otherLiabilities = sum(pfs?.otherLiabilities, 'currentBalance');
  const otherLiabilityNote = Number(pfs?.otherLiabilityNote) || 0;

  const totalAssets =
    cash + investments + otherAssets + businessNetWorth + realEstateValue;
  const totalLiabilities =
    revolvingCredit +
    installmentLoans +
    mortgageLoans +
    otherLiabilities +
    otherLiabilityNote;

  return {
    totalAnnualIncome: applicantIncome + spouseIncome,
    applicantAnnualIncome: applicantIncome,
    spouseAnnualIncome: spouseIncome,
    totalAssets,
    totalLiabilities,
    netWorth: totalAssets - totalLiabilities,
  };
}
